import React, { useState } from "react";
import PropTypes from "prop-types";
import { Image } from "@sparefoot/react-commons";
import { Modal } from "components/molecules";
import { Button } from "components/core/Button";
import { H2, Text } from "components/core/Typography";
import ChevronArrowIcon from "assets/icons/chevron-arrows.svg";
import MissingFacilityImage from "assets/images/missing-facility.jpg";

import "./FacilityGalleryModal.scss";

const FacilityGalleryModal = ({ isOpen, onClose, images, facilityName }) => {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    const allImages = images.length > 0 ? images : [MissingFacilityImage];
    const mainImage = allImages[selectedImageIndex];
    const totalImages = allImages.length;
    const isFirstImage = selectedImageIndex === 0;
    const isLastImage = selectedImageIndex === totalImages - 1;

    const handleThumbnailClick = (index) => {
        setSelectedImageIndex(index);
    };

    const handlePrevious = () => {
        if (!isFirstImage) {
            setSelectedImageIndex((prev) => prev - 1);
        }
    };

    const handleNext = () => {
        if (!isLastImage) {
            setSelectedImageIndex((prev) => prev + 1);
        }
    };

    const handleClose = () => {
        setSelectedImageIndex(0);
        onClose();
    };

    const toolbarContent = (
        <H2 className="facility-gallery-modal__title">{facilityName}</H2>
    );

    return (
        <Modal
            isOpen={isOpen}
            handleClose={handleClose}
            closable
            size={Modal.SIZES.LARGE}
            toolbarContent={toolbarContent}
            ariaLabel={`${facilityName} photo gallery`}
            className="facility-gallery-modal"
        >
            <div className="facility-gallery-modal__content">
                <div className="facility-gallery-modal__main-image">
                    <Image
                        key={selectedImageIndex}
                        src={mainImage}
                        alt={`${facilityName} - Image ${selectedImageIndex + 1}`}
                        className="facility-gallery-modal__image"
                    />
                    {totalImages > 1 && (
                        <div className="facility-gallery-modal__navigation facility-gallery-modal__navigation--desktop">
                            <Button
                                className="facility-gallery-modal__nav-btn"
                                onClick={handlePrevious}
                                label="Previous image"
                                disabled={isFirstImage}
                                fullWidth={false}
                            >
                                <Image src={ChevronArrowIcon} alt="" className="facility-gallery-modal__nav-icon" />
                            </Button>
                            <Text className="facility-gallery-modal__counter">
                                {selectedImageIndex + 1} / {totalImages}
                            </Text>
                            <Button
                                className="facility-gallery-modal__nav-btn"
                                onClick={handleNext}
                                label="Next image"
                                disabled={isLastImage}
                                fullWidth={false}
                            >
                                <Image src={ChevronArrowIcon} alt="" className="facility-gallery-modal__nav-icon facility-gallery-modal__nav-icon--right" />
                            </Button>
                        </div>
                    )}
                </div>

                {totalImages > 1 && (
                    <div className="facility-gallery-modal__thumbnails">
                        {allImages.map((image, index) => (
                            <Button
                                key={image}
                                className={`facility-gallery-modal__thumbnail ${
                                    index === selectedImageIndex ? "facility-gallery-modal__thumbnail--active" : ""
                                }`}
                                onClick={() => handleThumbnailClick(index)}
                                label={`View image ${index + 1}`}
                                fullWidth={false}
                            >
                                <Image
                                    src={image}
                                    alt={`${facilityName} thumbnail ${index + 1}`}
                                    className="facility-gallery-modal__thumbnail-image"
                                />
                            </Button>
                        ))}
                    </div>
                )}

                {totalImages > 1 && (
                    <div className="facility-gallery-modal__navigation facility-gallery-modal__navigation--mobile">
                        <Button
                            className="facility-gallery-modal__nav-btn facility-gallery-modal__nav-btn--text"
                            onClick={handlePrevious}
                            label="Previous image"
                            disabled={isFirstImage}
                            fullWidth={false}
                        >
                            <Image src={ChevronArrowIcon} alt="" className="facility-gallery-modal__nav-icon" />
                            <Text>Prev</Text>
                        </Button>
                        <Text className="facility-gallery-modal__counter">
                            {selectedImageIndex + 1} / {totalImages}
                        </Text>
                        <Button
                            className="facility-gallery-modal__nav-btn facility-gallery-modal__nav-btn--text"
                            onClick={handleNext}
                            label="Next image"
                            disabled={isLastImage}
                            fullWidth={false}
                        >
                            <Text>Next</Text>
                            <Image src={ChevronArrowIcon} alt="" className="facility-gallery-modal__nav-icon facility-gallery-modal__nav-icon--right" />
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
};

FacilityGalleryModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    images: PropTypes.arrayOf(PropTypes.string),
    facilityName: PropTypes.string
};

FacilityGalleryModal.defaultProps = {
    images: [],
    facilityName: "Facility"
};

export default FacilityGalleryModal;
